import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Paperclip, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AttachmentPanel } from "@/components/AttachmentPanel";
import { sanitizeHtml } from "@/lib/markdown";
import { LOG_KINDS, longDate, shortDate } from "@/lib/eventMeta";

const PER_PAGE = 5;

const kindLabel = (kind) => LOG_KINDS.find((k) => k.value === kind)?.label || kind;

const snippet = (html) =>
  sanitizeHtml(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

export const HearingLogList = ({
  logs = [],
  canWrite,
  onEdit,
  onDelete,
  onChanged,
  openLogId,
  onOpenedLog,
}) => {
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [phrase, setPhrase] = useState("");

  const found = useMemo(() => {
    const needle = phrase.trim().toLowerCase();
    if (!needle) return logs;
    return logs.filter((l) =>
      `${snippet(l.body_html)} ${kindLabel(l.kind)} ${l.date}`.toLowerCase().includes(needle),
    );
  }, [logs, phrase]);

  const maxOffset = Math.max(0, found.length - PER_PAGE);

  useEffect(() => {
    if (offset > maxOffset) setOffset(maxOffset);
  }, [offset, maxOffset]);

  // a search result deep-links straight to one entry, so open it and bring it into the window
  useEffect(() => {
    if (!openLogId || !logs.length) return;
    const at = logs.findIndex((l) => l.log_id === openLogId);
    if (at === -1) return;
    setPhrase("");
    setOffset(Math.min(at, Math.max(0, logs.length - PER_PAGE)));
    setOpenId(openLogId);
    onOpenedLog?.();
  }, [openLogId, logs]);

  const shown = useMemo(() => found.slice(offset, offset + PER_PAGE), [found, offset]);

  // logs arrive newest first, so the first one at or before the chosen day is the nearest match
  const jump = (value) => {
    if (!value || !found.length) return;
    const at = found.findIndex((l) => l.date <= value);
    setOffset(Math.min(at === -1 ? maxOffset : at, maxOffset));
  };

  const open = logs.find((l) => l.log_id === openId) || null;

  if (!logs.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="log-list-empty">
        No interim orders or directions recorded yet.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="log-list">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="label-caps">Search these logs</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="log-search"
                value={phrase}
                onChange={(e) => {
                  setPhrase(e.target.value);
                  setOffset(0);
                }}
                placeholder="a phrase from the order"
                className="rounded-sm h-10 text-sm pl-9 w-full sm:w-60"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-caps">Jump to a date</Label>
            <Input
              type="date"
              data-testid="log-jump-date"
              onChange={(e) => jump(e.target.value)}
              onClick={(e) => {
                try {
                  e.currentTarget.showPicker?.();
                } catch {
                  /* browsers that refuse showPicker still open on their own */
                }
              }}
              className="rounded-sm h-10 text-sm w-full sm:w-44"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground" data-testid="log-list-range">
            {found.length
              ? `${offset + 1}–${Math.min(found.length, offset + PER_PAGE)} of ${found.length}`
              : "no matches"}
            {phrase.trim() ? ` matching “${phrase.trim()}”` : ""}
          </span>
          <Button
            variant="outline"
            data-testid="log-prev-page"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PER_PAGE))}
            className="rounded-sm h-10 w-10 p-0"
            aria-label="Newer logs"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            data-testid="log-next-page"
            disabled={offset >= maxOffset}
            onClick={() => setOffset((o) => Math.min(maxOffset, o + PER_PAGE))}
            className="rounded-sm h-10 w-10 p-0"
            aria-label="Older logs"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {phrase.trim() && !found.length ? (
        <p className="text-sm text-muted-foreground" data-testid="log-search-empty">
          No hearing log on this matter mentions “{phrase.trim()}”.
        </p>
      ) : null}

      <div className="divide-y divide-border border border-border rounded-sm">
        {shown.map((log) => (
          <button
            key={log.log_id}
            data-testid={`log-row-${log.log_id}`}
            onClick={() => setOpenId(log.log_id)}
            className="w-full text-left px-4 py-3.5 hover:bg-accent/40 transition-colors duration-200"
          >
            <span className="flex flex-wrap items-center gap-2.5">
              <span className="chip chip-post">{kindLabel(log.kind)}</span>
              <span className="text-sm font-semibold">{longDate(log.date)}</span>
              {log.attachments?.length ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Paperclip className="h-3 w-3" /> {log.attachments.length}
                </span>
              ) : null}
              {log.transcribing ? (
                <span
                  data-testid={`log-writing-up-${log.log_id}`}
                  className="chip chip-session inline-flex items-center gap-1.5"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Being written up
                </span>
              ) : null}
            </span>
            <span
              className="rich-text rich-text-preview mt-1.5 text-sm text-muted-foreground line-clamp-2 pointer-events-none"
              data-testid={`log-snippet-${log.log_id}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(log.body_html) || "—" }}
            />
          </button>
        ))}
      </div>

      <Dialog open={Boolean(open)} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent
          className="rounded-sm max-w-2xl max-h-[75vh] overflow-y-auto"
          data-testid="log-detail-dialog"
        >
          {open ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{longDate(open.date)}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="chip chip-post">{kindLabel(open.kind)}</span>
                  {open.next_date ? (
                    <span
                      data-testid={`log-detail-next-date-${open.log_id}`}
                      className="chip chip-session"
                    >
                      Adjourned to {shortDate(open.next_date)}
                      {open.next_start_time ? ` · ${open.next_start_time}` : ""}
                    </span>
                  ) : null}
                </div>
                <div
                  data-testid="log-detail-body"
                  className="rich-text text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(open.body_html) }}
                />
                <AttachmentPanel
                  logId={open.log_id}
                  attachments={open.attachments || []}
                  canWrite={canWrite}
                  onChange={onChanged}
                  compact
                />
                <p className="text-xs text-muted-foreground">Recorded by {open.author_name}</p>
                {canWrite ? (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      data-testid={`log-detail-edit-${open.log_id}`}
                      onClick={() => {
                        setOpenId(null);
                        onEdit(open);
                      }}
                      className="rounded-full h-11 px-6"
                    >
                      Edit this log
                    </Button>
                    <Button
                      variant="outline"
                      data-testid={`log-detail-delete-${open.log_id}`}
                      onClick={() => {
                        setOpenId(null);
                        onDelete(open.log_id);
                      }}
                      className="rounded-full h-11 px-5 text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
